import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import I18NextHttpBackend from 'i18next-http-backend';
import i18next from 'i18next';
import type { LanguageDefinition } from '@jwp/ott-common/types/i18n';
import { filterEnabledLanguages, getI18nSupportedLanguages } from '@jwp/ott-common/src/utils/i18n';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';

import { NAMESPACES } from './resources';

// This list contains all languages that are supported by the OTT Web app by default
// To enable the language, make sure that the language code is added to the `APP_ENABLED_LANGUAGES` environment variable
// Before adding a defined language, ensure that the translation files are added to the `./public/locales/${code}` folder
export const DEFINED_LANGUAGES: LanguageDefinition[] = [
  {
    code: 'en',
    displayName: 'English',
  },
  {
    code: 'en-GB',
    displayName: 'English (GB)',
  },
  {
    code: 'en-US',
    displayName: 'English (US)',
  },
  {
    code: 'es',
    displayName: 'Español',
  },
  {
    code: 'th',
    displayName: 'ภาษาไทย',
  },
  {
    code: 'nl',
    displayName: 'Nederlands',
  },
  {
    code: 'nl-NL',
    displayName: 'Nederlands',
  },
  {
    code: 'nl-BE',
    displayName: 'Vlaams',
  },
  {
    code: 'fr',
    displayName: 'Français',
  },
  {
    code: 'de',
    displayName: 'Deutsch',
  },
  {
    code: 'pap',
    displayName: 'Papiamentu',
  },
  {
    code: 'tr',
    displayName: 'Türkçe',
  },
  {
    code: 'pl',
    displayName: 'Polski',
  },
  {
    code: 'fa',
    displayName: 'فارسی',
  },
  {
    code: 'ar',
    displayName: 'عربي',
  },
  {
    code: 'ar-MA',
    displayName: 'العربية (المغرب)',
  },
  {
    code: 'ar-SY',
    displayName: 'العربية (سوريا)',
  },
];

const initI18n = async () => {
  const enabledLanguages = import.meta.env.APP_ENABLED_LANGUAGES?.split(',') || [];
  const defaultLanguage = import.meta.env.APP_DEFAULT_LANGUAGE || 'en';

  const supportedLanguages = filterEnabledLanguages(DEFINED_LANGUAGES, enabledLanguages);

  useConfigStore.setState({ supportedLanguages });

  if (!supportedLanguages.some(({ code }) => code === defaultLanguage)) {
    throw new Error(`The default language is not enabled: ${defaultLanguage}`);
  }

  const i18nSupportedLngs = getI18nSupportedLanguages(DEFINED_LANGUAGES);

  await i18next
    .use(I18NextHttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs: i18nSupportedLngs,
      fallbackLng: defaultLanguage.split('-')[0],
      load: 'languageOnly',
      // this option ensures that empty strings in translations will fall back to the default language
      returnEmptyString: false,
      ns: NAMESPACES,
      defaultNS: 'common',
      fallbackNS: 'common',
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: 'jwapp.language',
        lookupQuerystring: 'lng',
        convertDetectedLanguage(lng: string) {
          // this convert language acts as a guard to prevent a mismatch between language codes (en -> en-US)
          // when the detected language uses a non-region language, it is accepted but for some configurations we only
          // support region based languages.
          const isSupported = supportedLanguages.some((def) => def.code === lng);
          return isSupported ? lng : '';
        },
      },
      react: {
        // disabled suspense to prevent re-loading the app while loading the resources
        useSuspense: false,
      },
    });
};

export default initI18n;
